// src/Login.js
import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from './AuthContext';
import logo from './assets/SnapConX-icon.png';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import './Login.css';
import { auth } from './config/firebase';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // If user is already logged in, redirect to dashboard
  if (currentUser) {
    return <Navigate to="/dashboard" />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      switch (error.code) {
        case 'auth/user-not-found':
          setErrorMessage('No account found with this email. Please check or sign up.');
          break;
        case 'auth/wrong-password':
          setErrorMessage('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setErrorMessage('Invalid email format.');
          break;
        case 'auth/too-many-requests':
          setErrorMessage('Too many attempts. Please try again later.');
          break;
        case 'auth/invalid-credential':
          setErrorMessage('Invalid email or password. Please try again.');
          break;
        default:
          setErrorMessage('Login failed. Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-content">
          {/* Left Side - Branding */}
          <div className="login-branding">
            <div className="branding-inner">
              <img src={logo} alt="SnapConX logo" className="login-logo" />
              <h1 className="login-app-name">SnapConX</h1>
              <p className="login-tagline">Connect • Share • Engage</p>
              <div className="branding-features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Secure & Trusted</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Easy Booking</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="login-form-section">
            <div className="form-card">
              <h2 className="welcome-text">Welcome Back</h2>
              <p className="welcome-subtitle">Sign in to continue to your account</p>

              <form onSubmit={handleLogin} className="login-form">
                <div className="input-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-container">
                    <FiMail className="input-icon" />
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setErrorMessage(''); }}
                      className="form-input"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-container">
                    <FiLock className="input-icon" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setErrorMessage(''); }}
                      className="form-input"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="eye-button"
                      onClick={() => setShowPassword(prev => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {errorMessage && (
                  <div className="error-message">
                    <span className="error-icon">⚠</span>
                    {errorMessage}
                  </div>
                )}

                <div className="form-options">
                  <label className="remember-me">
                    <input type="checkbox" />
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    className="forgot-password"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <div className="divider">
                <span>or</span>
              </div>

              <div className="app-download-section">
                <h3 className="download-title">New to SnapConX?</h3>
                <p className="download-message">
                  Download our mobile app to create your account and get started!
                </p>
                <p className="download-submessage">
                  Once registered, you can login here with your credentials.
                </p>
                <div className="app-store-buttons">
                  <a 
                    href="https://apps.apple.com/app/id6755649903" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="store-button apple-button"
                  >
                    <FaApple className="store-icon" />
                    <div className="store-text">
                      <span className="store-label">Download on the</span>
                      <span className="store-name">App Store</span>
                    </div>
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.authurton.dotdashmobile" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="store-button google-button"
                  >
                    <FaGooglePlay className="store-icon" />
                    <div className="store-text">
                      <span className="store-label">Get it on</span>
                      <span className="store-name">Google Play</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}